/**
 * WHERE EVERY SHOPPER WENT, WHAT THEY TAPPED, HOW FAR THEY SCROLLED, WHERE THEY STOPPED — every visitor.
 *
 * "Something is wrong with our flow product more than the ads" (the owner, 2026-10-05). The journey
 * (utils/shopJourney.js) says which pages a shopper opened and which photos they moved through; the
 * checkout says which field they were in (utils/checkoutTelemetry.js). Neither says how far down a
 * product page they got, whether the price or the colours or the reviews were ever on screen, which
 * button they pressed and which they never found, or whether the page they left for was ever opened.
 * This records that, for EVERY visitor — an ad's or not — and the admin's Checkout Trace reads it
 * (stylon-server ads/journeys/shop-movement.core.ts).
 *
 * THE EVENTS, each one row in the shop's batched POST /analytics/events/batch (api/storeApi.js),
 * under the business's id and the visitor key every other step carries:
 *
 *   shop_scroll      how far down the page (max %) and which named sections have been on screen —
 *                    at the 25 / 50 / 75 / 100 % milestones, once each, and once more as the page is
 *                    left or hidden (`final`). Never per scroll tick.
 *   shop_tap         one per tap on a named control: `target` (buy_now, add_to_cart, color, size,
 *                    qty_inc, tab, product_card, contact_order_whatsapp, back, gallery_swipe, field…)
 *                    and a short `detail` (the colour's name, the tab's key, a product id, a field's
 *                    NAME). Named by the control's `data-move` or its `data-testid`; a control with
 *                    neither is `button` or `link`, and a link's detail is its path without any query.
 *   shop_dwell       the page's summary as it is left or hidden: visible seconds (capped at 10 min,
 *                    by the visibility API), max scroll %, the sections reached in order, the first
 *                    40 taps in order, the last tap and the last section.
 *   shop_nav         from one page to the next: `from` → `to`, what triggered it (tap / back / link /
 *                    load / leave) and `continued` — true when the next page opened in this session,
 *                    false when the page was left for good (pagehide).
 *   checkout_scroll  the same two, on the checkout page, under their own names so the checkout's
 *   checkout_tap     field ORDER (every focus is a `field` tap with the field's name) reads apart.
 *   session_end      the last page, section and tap, and the visible seconds, pages and taps of the
 *                    session — sent whenever the page is hidden or left; the reader keeps the last.
 *
 * THE RULES:
 *   · every visitor, on the business's own pages, once the page has said whose it is
 *     (useSalonWebTheme → journeySetBusiness → movementSetBusiness); the first page's events wait
 *     for that, up to MAX_HELD of them;
 *   · a session is the tab, renewed after 30 idle minutes (sessionStorage), so a shopper who comes
 *     back tomorrow is a new session of the same visitor;
 *   · at most MAX_TAPS_PER_PAGE tap events a page; the scroll is read at most every 250 ms and sent
 *     at most 5 times a page; everything leaves in the same batch as every other event;
 *   · NOTHING PERSONAL: no text content, no field VALUE, no query string, no element text. A field is
 *     its name (checkoutTelemetry.fieldOfTarget); a link is its path; a control is its static id;
 *   · never from a browser driven by automation (`navigator.webdriver`), the journey's rule;
 *   · never in a way that can throw into the page.
 */
import { v4 as uuidv4 } from 'uuid';
import { beforeStoreEventsLeave, queueStoreEvent, visitorKeyForEvents } from '../api/storeApi';
import { envOf, fieldOfTarget } from './checkoutTelemetry';
import { isDomainMode } from './domainMode';
import { journeyPageOf } from './shopPageOf';
import { shopSlugOfPath } from './shopSurface';

export const MOVEMENT_EVENT_TYPES = ['shop_scroll', 'shop_tap', 'shop_dwell', 'shop_nav', 'checkout_scroll', 'checkout_tap', 'session_end'];
export const SCROLL_MILESTONES = [25, 50, 75, 100];
/** The longest look at one page that is kept: ten minutes. Longer is a tab left open, not a reading. */
export const DWELL_CAP_S = 600;
/** The longest session: two hours of visible time. */
export const SESSION_CAP_S = 7200;
/** Tap events one page may send; the dwell's list keeps the first TAPS_KEPT. */
export const MAX_TAPS_PER_PAGE = 200;
export const TAPS_KEPT = 40;
/** A tab idle this long starts a new session. */
export const SESSION_IDLE_MS = 30 * 60 * 1000;
/** Events the first page holds while the business is not yet known. */
const MAX_HELD = 50;
/** A tap or a back this close before the page changed is what changed it. */
const TRIGGER_WINDOW_MS = 2000;
const SCROLL_TICK_MS = 250;
/** A hide and a pagehide this close together are one leaving. */
const SAME_LEAVING_MS = 1500;
const SESSION_KEY = 'stylon:movement:session';
export const SECTION_NAME = /^[a-z][a-z0-9_]{0,31}$/;
const TARGET_RE = /[^a-z0-9_:.]/g;
const DETAIL_RE = /[^a-zA-Z0-9_:./-]/g;
const FORM_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
const ID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|^\d{1,6}$/i;
/** A control whose test id is `<axis>-<choice>`: the choice is the detail, not part of the name. */
const AXES = new Set(['color', 'size', 'style', 'tab', 'thumb', 'step']);

/**
 * THE SECTIONS OF EACH KIND OF PAGE, top to bottom, by the marks the pages already carry. A section
 * is "reached" once its top has entered the lower 85 % of the screen. Any element marked
 * `data-move-section="<name>"` is a section too, so a new block needs one attribute and no entry here.
 */
export const SECTIONS = {
  product: [
    ['hero', '[data-testid="product-detail-hero"]'],
    /* The STAGE, not the gallery wrapper: `.gallery` is `display: contents`, so it is not a box
       at all and its rect is all zeros — which this file reads as top 0, i.e. "already seen".
       Every visit recorded the gallery section on its first measurement. */
    ['gallery', '[data-testid="product-detail-carousel"]'],
    ['price', '[data-testid="product-detail-price"]'],
    ['colours', '[data-testid="product-detail-variant-picker"]'],
    ['quantity', '[data-testid="product-detail-qty-block"]'],
    /* THE ROW, NOT THE BUTTONS IN IT (2026-10-07). On a phone those two buttons are
       `display: none` — the sticky bar carries the real ones — and a hidden element's
       `getBoundingClientRect()` is all zeros. `measure()` below records a section as soon as
       its top is inside the lower 85% of the screen, and 0 always is: so every phone visitor
       was logged as having reached "buy" on the first tick, before scrolling at all. A section
       marker has to be an element that is laid out, which the row is (zero height, real top).
       jsdom gives every element a zero rect, so no unit test can see this; it was found by
       walking this list against the built page in a browser. */
    ['buy', '[data-testid="product-detail-buy-row"]'],
    ['description', '[data-testid="product-detail-tabs"]'],
    ['shop', '[data-testid="product-detail-visit-shop"]'],
    ['reviews', '[data-testid="product-detail-reviews-section"]'],
    ['related', '[data-testid="product-detail-related"]'],
    ['footer', '[data-testid="shop-privacy-notice"]'],
  ],
  checkout: [
    ['steps', '[data-testid="checkout-steps"]'],
    ['contact', '[data-testid="checkout-name"], [data-testid="checkout-contact-done"], [data-testid="co-name"]'],
    ['address', '[data-testid="checkout-address-input"], [data-testid="checkout-address-label"], [data-testid="checkout-address-new"], [data-testid="checkout-address-street"]'],
    ['delivery', '[data-testid="checkout-delivery-row"]'],
    ['notes', '[data-testid="checkout-add-order-note"], [data-testid="checkout-order-note"]'],
    ['basket', '[data-testid="checkout-basket-lines"]'],
    ['summary', '[data-testid="checkout-subtotal"]'],
    ['payment', '[data-testid="checkout-pay-apple"], [data-testid="checkout-pay-google"], [data-testid="checkout-invoice-section"], [data-testid="checkout-secure-note"]'],
    ['action', '[data-testid="checkout-action-block"], [data-testid="checkout-submit"]'],
  ],
  shop: [
    ['header', '[data-testid="shop-scroll-header"]'],
    ['products', '[data-testid="shop-products-area"]'],
    ['footer', '[data-testid="shop-privacy-notice"]'],
  ],
};
const SHOP_PAGE_TYPES = new Set(['shop_home', 'category', 'brand', 'collection', 'search']);

/**
 * `data-testid` prefixes a tap is named by, and the word each one becomes. 'rec-mini-card-<id>' is a
 * `product_card` with the id as detail; 'product-detail-color-black' is `color` with `black`.
 */
const TESTID_PREFIXES = [
  ['rec-mini-card-add-', 'card_add'], ['rec-mini-card-', 'product_card'], ['shop-product-card-', 'product_card'],
  ['shop-strip-card-', 'strip_card'], ['shop-search-result-', 'search_result'], ['product-detail-', ''],
  ['checkout-', ''], ['co-', ''], ['cart-', 'cart_'], ['shop-scroll-', 'bar_'], ['shop-', ''],
];

const now = () => Date.now();
const automated = () => typeof navigator !== 'undefined' && navigator.webdriver === true;
const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/* ── Whose pages, and which ──────────────────────────────────────────── */

let business = null;
/** The business whose pages these are — told by the page that loaded it, through journeySetBusiness. */
export function movementSetBusiness({ salonId, slug } = {}) {
  business = salonId ? { salonId: String(salonId), slug: slug ? String(slug) : null } : null;
  if (business) release();
}
function salonFor(route) {
  if (!business) return null;
  if (!isDomainMode()) {
    const slug = shopSlugOfPath(route);
    if (!business.slug || !slug || slug.toLowerCase() !== business.slug.toLowerCase()) return null;
  }
  return business.salonId;
}
const routeOf = (pathname) => String(pathname || '').split(/[?#]/)[0].slice(0, 200);

/* ── The session: this tab, until it has been idle half an hour ───────── */

let sess = null;
function readSession() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    if (stored && typeof stored.id === 'string' && now() - Number(stored.at) <= SESSION_IDLE_MS) return stored;
  } catch (_) { /* a tab that cannot remember starts afresh */ }
  return null;
}
function persistSession() {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess)); } catch (_) { /* then it lives in memory */ }
}
function session() {
  const at = now();
  if (!sess || at - sess.at > SESSION_IDLE_MS) {
    sess = readSession() || { id: uuidv4(), startedAt: at, pages: 0, taps: 0, seconds: 0 };
  }
  sess.at = at;
  return sess;
}

/* ── The page being looked at ─────────────────────────────────────────── */

let page = null;
let held = [];
let watching = false;
let stopLeaving = null;
let scrollTimer = null;
let lastScrollTick = 0;
let settleTimer = null;
let pendingTrigger = null;
let lastHideAt = 0;
let env = null;
function envClass() {
  if (!env) {
    try { const e = envOf(); env = { handheld: e.handheld, os: e.os, browser: e.browser }; } catch (_) { env = {}; }
  }
  return env;
}

function pauseClock() {
  if (page && page.visibleSince != null) {
    page.visibleMs += now() - page.visibleSince;
    page.visibleSince = null;
  }
}
function resumeClock() {
  if (page && page.visibleSince == null && visible()) page.visibleSince = now();
}
function pageSeconds() {
  if (!page) return 0;
  const ms = page.visibleMs + (page.visibleSince != null ? now() - page.visibleSince : 0);
  return Math.min(DWELL_CAP_S, Math.max(0, Math.round(ms / 1000)));
}

/** The payload every movement event starts from. */
function base() {
  const s = session();
  return {
    sessionId: s.id, route: page.route, pageType: page.pageType, productId: page.productId, variantId: page.variantId,
    n: page.n, at: now(), ...envClass(),
  };
}

function send(eventType, payload, salonId) {
  queueStoreEvent({ source: 'store', eventType, salonId, visitorKey: visitorKeyForEvents(), payload });
}
/** Send an event, or hold it until the business is known. */
function emit(eventType, fields) {
  try {
    if (automated() || !page) return;
    const payload = { ...base(), ...fields };
    const salonId = salonFor(page.route);
    if (salonId) { send(eventType, payload, salonId); persistSession(); return; }
    if (!business && held.length < MAX_HELD) held.push({ eventType, payload });
  } catch (_) { /* never throw from instrumentation */ }
}
function release() {
  const waiting = held;
  held = [];
  waiting.forEach(({ eventType, payload }) => {
    const salonId = salonFor(payload.route);
    if (salonId) send(eventType, payload, salonId);
  });
}

const scrollType = () => (page && page.pageType === 'checkout' ? 'checkout_scroll' : 'shop_scroll');
const tapType = () => (page && page.pageType === 'checkout' ? 'checkout_tap' : 'shop_tap');

/* ── How far, and which sections ──────────────────────────────────────── */

/* A page shorter than the screen has no "how far down" — all of it is already in view. */
const SCROLLABLE_SLACK_PX = 24;

/**
 * How far down the page has been seen, or NULL when the question does not apply yet.
 *
 * NULL IS THE WHOLE POINT, and it is a bug fix (2026-10-08). This returned a NUMBER in every case,
 * and `measure()` keeps `Math.max` of it — so the very first call, which happens the instant the
 * route changes and BEFORE the product has painted anything, measured an empty shell whose
 * scrollHeight equals the viewport and returned 100. The max then held 100 for the rest of the
 * visit, whatever the shopper actually did.
 *
 * Read off production the same day: of 258 product dwells from DK's ads, 257 reported 75-100% and
 * the average depth was 99%. Not one visitor in fourteen days appeared to stop partway down a
 * product page, which is not a finding about shoppers — it is a number that could not come out any
 * other way. See [[feedback-ask-whether-a-check-could-fail-at-all]].
 */
function scrolledPct() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const doc = document.documentElement;
  const height = Math.max(doc?.scrollHeight || 0, document.body?.scrollHeight || 0);
  const view = window.innerHeight || 0;
  if (!height || !view) return null;
  // Not taller than the screen: either nothing has painted yet, or the page genuinely fits. Both
  // mean "no depth to report", and both are recorded as `scrollable: false` rather than as 100%.
  if (height <= view + SCROLLABLE_SLACK_PX) return null;
  const seen = (window.scrollY || window.pageYOffset || 0) + view;
  return Math.max(0, Math.min(100, Math.round((seen / height) * 100)));
}

/** The named sections of this page, top to bottom: the kind's own, then any `data-move-section`. */
export function sectionsOnPage(pageType, doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.querySelector !== 'function') return [];
  const kind = pageType === 'product' ? 'product' : pageType === 'checkout' ? 'checkout' : SHOP_PAGE_TYPES.has(pageType) ? 'shop' : null;
  const out = [];
  (kind ? SECTIONS[kind] : []).forEach(([name, selector]) => {
    const el = doc.querySelector(selector);
    if (el) out.push([name, el]);
  });
  doc.querySelectorAll('[data-move-section]').forEach((el) => {
    const name = String(el.getAttribute('data-move-section') || '').toLowerCase();
    if (SECTION_NAME.test(name) && !out.some(([n]) => n === name)) out.push([name, el]);
  });
  return out;
}

/** Note every section whose top has come into the lower 85 % of the screen, in the order it happened. */
function measure() {
  if (!page || typeof window === 'undefined') return;
  const limit = (window.innerHeight || 0) * 0.85;
  sectionsOnPage(page.pageType).forEach(([name, el]) => {
    if (page.sections.includes(name)) return;
    let top;
    try { top = el.getBoundingClientRect().top; } catch (_) { return; }
    if (top < limit) { page.sections.push(name); page.lastSection = name; }
  });
  const pct = scrolledPct();
  // Only a real reading moves the maximum. A null says the page is not taller than the screen yet,
  // which is a fact about the page and never a depth the shopper reached.
  if (pct !== null) { page.scrollable = true; page.maxPct = Math.max(page.maxPct, pct); }
}

function scrollTick() {
  scrollTimer = null;
  lastScrollTick = now();
  if (!page) return;
  measure();
  SCROLL_MILESTONES.forEach((m) => {
    if (page.maxPct >= m && !page.milestones.has(m)) {
      page.milestones.add(m);
      emit(scrollType(), { maxPct: page.maxPct, milestone: m, sections: page.sections.slice() });
    }
  });
}
function onScroll() {
  if (!page || scrollTimer) return;
  const wait = Math.max(0, SCROLL_TICK_MS - (now() - lastScrollTick));
  scrollTimer = setTimeout(scrollTick, wait);
}

/* ── Taps ─────────────────────────────────────────────────────────────── */

const cleanTarget = (s) => String(s || '').toLowerCase().replace(/-/g, '_').replace(TARGET_RE, '').slice(0, 40);
const cleanDetail = (s) => (s == null || s === '' ? undefined : String(s).replace(DETAIL_RE, '').slice(0, 60) || undefined);

/** A link's path on this site, or its host elsewhere — never a query string. */
function linkDetail(href) {
  try {
    const url = new URL(String(href || ''), window.location.href);
    return url.origin === window.location.origin ? url.pathname.slice(0, 60) : url.hostname.slice(0, 60);
  } catch (_) { return undefined; }
}

/**
 * The name of what was tapped: its `data-move` (with `data-move-detail` / `-product` / `-variant`), else
 * its `data-testid` under a known prefix, else `link` / `button`. Null when nothing named was tapped.
 */
export function tapTargetOf(el) {
  if (!el || typeof el.closest !== 'function') return null;
  const moved = el.closest('[data-move]');
  if (moved) {
    const target = cleanTarget(moved.getAttribute('data-move'));
    if (!target || target === 'ignore') return null;
    return {
      target,
      detail: cleanDetail(moved.getAttribute('data-move-detail')),
      productId: cleanDetail(moved.getAttribute('data-move-product')),
      variantId: cleanDetail(moved.getAttribute('data-move-variant')),
    };
  }
  const tagged = el.closest('[data-testid]');
  if (tagged) {
    const id = String(tagged.getAttribute('data-testid') || '');
    const found = TESTID_PREFIXES.find(([prefix]) => id.startsWith(prefix));
    if (found) {
      const [prefix, word] = found;
      const rest = id.slice(prefix.length);
      if (word && ID_LIKE.test(rest)) return { target: word, detail: cleanDetail(rest) };
      const parts = rest.split('-');
      const last = parts[parts.length - 1];
      if (!word && parts.length > 1 && AXES.has(parts[0])) return { target: parts[0], detail: cleanDetail(parts.slice(1).join('-')) };
      if (parts.length > 1 && ID_LIKE.test(last)) return { target: cleanTarget(word + parts.slice(0, -1).join('_')), detail: cleanDetail(last) };
      return { target: cleanTarget(word + rest) || undefined, detail: undefined };
    }
  }
  const link = el.closest('a[href]');
  if (link) return { target: 'link', detail: linkDetail(link.getAttribute('href')) };
  const button = el.closest('button, [role="button"]');
  if (button) return { target: 'button', detail: cleanDetail(button.id || button.getAttribute('name')) };
  return null;
}

function recordTap({ target, detail, productId, variantId }) {
  if (!page || !target) return;
  const s = session();
  page.tapCount += 1;
  s.taps += 1;
  const word = detail && detail.length <= 16 && (target === 'field' || target === 'tab' || target === 'color' || target === 'size' || target === 'style') ? `${target}:${detail}` : target;
  page.lastTap = word;
  if (page.taps.length < TAPS_KEPT) page.taps.push(word);
  if (page.tapCount > MAX_TAPS_PER_PAGE) return;
  emit(tapType(), {
    target, ...(detail ? { detail } : {}), i: page.tapCount,
    ...(productId ? { productId } : {}), ...(variantId ? { variantId } : {}),
  });
}

/** A tap the page reports itself — a gallery swipe (utils/shopJourney.js journeyGallery), a pinch. */
export function movementTap(target, { detail, productId, variantId } = {}) {
  try {
    const t = cleanTarget(target);
    if (!t) return;
    recordTap({ target: t, detail: cleanDetail(detail), productId: cleanDetail(productId), variantId: cleanDetail(variantId) });
  } catch (_) { /* never throw from instrumentation */ }
}

function onClick(e) {
  try {
    if (!page) return;
    const el = e.target;
    if (!el || typeof el.closest !== 'function') return;
    // A form field is said by its focus (below), and the gallery says its own moves (journeyGallery).
    if (FORM_TAGS.has(String(el.tagName || '').toUpperCase())) return;
    if (el.closest('[data-testid="product-detail-gallery"]')) return;
    const t = tapTargetOf(el);
    if (!t || !t.target) return;
    recordTap(t);
    pendingTrigger = { kind: t.target === 'back' ? 'back' : t.target === 'link' ? 'link' : 'tap', at: now() };
  } catch (_) { /* never throw from instrumentation */ }
}

/** A field entered: on the checkout its trace name (never its value); elsewhere the control's name. */
function onFocus(e) {
  try {
    if (!page) return;
    const el = e.target;
    if (!el || !FORM_TAGS.has(String(el.tagName || '').toUpperCase())) return;
    const name = page.pageType === 'checkout' ? fieldOfTarget(el) : (tapTargetOf(el) || {}).target;
    if (!name) return;
    recordTap({ target: 'field', detail: cleanDetail(name) });
  } catch (_) { /* never throw from instrumentation */ }
}

function onPopState() { pendingTrigger = { kind: 'back', at: now() }; }

function onVisibility() {
  if (!page) return;
  if (visible()) resumeClock(); else pauseClock();
}

/* ── Leaving: the page's summary, the session's last word ─────────────── */

function sayPage({ final }) {
  pauseClock();
  measure();
  const seconds = pageSeconds();
  emit(scrollType(), { maxPct: page.maxPct, scrollable: page.scrollable, final: true, sections: page.sections.slice() });
  emit('shop_dwell', {
    seconds, maxPct: page.maxPct, scrollable: page.scrollable,
    sections: page.sections.slice(), taps: page.taps.slice(), tapCount: page.tapCount,
    lastTap: page.lastTap, lastSection: page.lastSection, final, beats: page.beats,
  });
  return seconds;
}

/* ── THE VISIT SAYS ITSELF WHILE IT HAPPENS, NOT ONLY ON THE WAY OUT ──────────────────────────────
 *
 * Measured on production 2026-10-08, DK's ads over fourteen days: 1,606 people reached the shop and
 * 1,479 opened a product, and only 97 of them — SIX PER CENT — ever produced a `shop_dwell`,
 * `shop_scroll` or `session_end`. For the other 94% there is no answer at all to "how long did they
 * stay and how far did they get", which is the question the owner asked.
 *
 * IT IS NOT THE IN-APP BROWSER, which was the obvious suspect and is wrong: in-app visitors produced
 * a dwell 7.6% of the time and real-browser visitors 5.7%. It is not the sending either — the queue
 * leaves by `fetch` with `keepalive` on both `pagehide` and `visibilitychange`. It is that those two
 * events simply do not fire for most real people on a phone: the app is swiped away, the tab is
 * discarded, the OS reclaims the page. Everything this file knows was then only ever spoken at a
 * moment that usually never comes.
 *
 * So the page now says where it has got to at fixed marks while it is still open. A visit of four
 * seconds leaves a record of four seconds; one of two minutes leaves five records and the last one
 * is still true if the shopper is never heard from again. The leave events stay exactly as they
 * were — they are better when they arrive, because only they can say `final` — this just stops them
 * being the ONLY way a visit is ever counted.
 *
 * The marks are sparse on purpose: six per page at most, and only while the page is actually
 * visible, so a tab left open in the background costs nothing. */
const HEARTBEAT_MARKS_S = [3, 10, 30, 60, 120, 300];
let beatTimer = null;

function stopBeating() {
  if (beatTimer) { clearTimeout(beatTimer); beatTimer = null; }
}

function beat() {
  beatTimer = null;
  if (!page) return;
  const mark = HEARTBEAT_MARKS_S[page.beats];
  if (mark === undefined) return;
  // The clock only runs while the page is visible, so a backgrounded tab never reaches the mark and
  // never sends. Re-arm and wait rather than report a second nobody spent looking.
  if (pageSeconds() < mark) { scheduleBeat(); return; }
  measure();
  page.beats += 1;
  emit('shop_dwell', {
    seconds: pageSeconds(), maxPct: page.maxPct, scrollable: page.scrollable,
    sections: page.sections.slice(), taps: page.taps.slice(), tapCount: page.tapCount,
    lastTap: page.lastTap, lastSection: page.lastSection, final: false, beats: page.beats, mark,
  });
  scheduleBeat();
}

function scheduleBeat() {
  stopBeating();
  if (!page || page.beats >= HEARTBEAT_MARKS_S.length) return;
  const wait = Math.max(1000, (HEARTBEAT_MARKS_S[page.beats] - pageSeconds()) * 1000);
  beatTimer = setTimeout(beat, wait);
}

function saySession(seconds) {
  const s = session();
  emit('session_end', {
    lastRoute: page.route, lastPageType: page.pageType, lastProductId: page.productId, lastSection: page.lastSection, lastTap: page.lastTap,
    seconds: Math.min(SESSION_CAP_S, s.seconds + seconds), pages: s.pages, taps: s.taps,
  });
}

/** The page is hidden (reason 'hidden') or left for good (reason 'pagehide') — asked by storeApi before the queue leaves. */
function onLeave(reason) {
  try {
    if (!page) return;
    const at = now();
    if (reason === 'pagehide') {
      const said = at - lastHideAt < SAME_LEAVING_MS;
      const seconds = said ? pageSeconds() : sayPage({ final: true });
      emit('shop_nav', { from: page.route, fromType: page.pageType, to: null, toType: null, trigger: 'leave', continued: false, seconds });
      if (!said) saySession(seconds);
      return;
    }
    lastHideAt = at;
    saySession(sayPage({ final: false }));
  } catch (_) { /* never throw from instrumentation */ }
}

function watch() {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('popstate', onPopState);
  document.addEventListener('click', onClick, true);
  document.addEventListener('focusin', onFocus, true);
  document.addEventListener('visibilitychange', onVisibility);
  stopLeaving = beforeStoreEventsLeave(onLeave);
}

/**
 * The address changed. The page before it is summed up and the move named; the new page starts its
 * clock. `restored` is a page the browser brought back from its back-forward cache.
 */
export function movementPageChange({ pathname, search, restored } = {}) {
  try {
    const where = journeyPageOf(pathname, search);
    const route = routeOf(pathname);
    let trigger = 'load';
    if (page) {
      const pending = pendingTrigger && now() - pendingTrigger.at <= TRIGGER_WINDOW_MS ? pendingTrigger.kind : 'other';
      trigger = pending;
      if (page.route !== route || page.variantId !== (where && where.variantId)) {
        const seconds = sayPage({ final: true });
        const s = session();
        s.seconds = Math.min(SESSION_CAP_S, s.seconds + seconds);
        emit('shop_nav', { from: page.route, fromType: page.pageType, to: where ? route : null, toType: where ? where.pageType : null, trigger, continued: true, seconds });
      }
    } else if (restored) trigger = 'back';
    pendingTrigger = null;
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    if (!where) { page = null; return; }
    const s = session();
    s.pages += 1;
    page = {
      route, pageType: where.pageType, productId: where.productId, variantId: where.variantId, n: s.pages, trigger,
      visibleSince: visible() ? now() : null, visibleMs: 0, maxPct: 0, sections: [], milestones: new Set(), taps: [], tapCount: 0,
      lastTap: null, lastSection: null, scrollable: false, beats: 0,
    };
    watch();
    measure();
    scheduleBeat();
    // The page's blocks arrive after its data does: look once more when they have had time to paint.
    settleTimer = setTimeout(() => { settleTimer = null; measure(); }, 1500);
  } catch (_) { /* never throw from instrumentation */ }
}

/** Tests only: a fresh session, page and listeners. */
export function __resetShopMovement() {
  if (watching && typeof window !== 'undefined') {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('popstate', onPopState);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('focusin', onFocus, true);
    document.removeEventListener('visibilitychange', onVisibility);
  }
  stopBeating();
  if (stopLeaving) stopLeaving();
  if (scrollTimer) clearTimeout(scrollTimer);
  if (settleTimer) clearTimeout(settleTimer);
  stopLeaving = null; scrollTimer = null; settleTimer = null; lastScrollTick = 0;
  watching = false; page = null; held = []; business = null; sess = null; pendingTrigger = null; lastHideAt = 0; env = null;
}
